import React, { createContext, useContext, useState, useMemo } from 'react';
import { Gym, FilterState } from '../types';

/**
 * Directory state shared between the persistent <DirectoryPage> overlay and the
 * gym-profile route wrappers. Relocated verbatim from AppRoutes' useState atoms
 * (item 50) to collapse the App→DirectoryPage prop+setter drill — consumers now
 * read/write directly instead of round-tripping via callback-up props.
 *
 * State-only: holds the 4 cache/filter atoms + their setters. `selectedGym` is
 * intentionally NOT here (out of scope; stays in AppRoutes). SSR-safe — only the
 * relocated useState defaults; no window/document/localStorage at init or render.
 */
interface DirectoryContextValue {
    activeFilters: FilterState | undefined;
    setActiveFilters: React.Dispatch<React.SetStateAction<FilterState | undefined>>;
    cachedSearchResults: Gym[];
    setCachedSearchResults: React.Dispatch<React.SetStateAction<Gym[]>>;
    cachedSearchLocation: string;
    setCachedSearchLocation: React.Dispatch<React.SetStateAction<string>>;
    cachedSearchCenter: { lat: number; lng: number } | null;
    setCachedSearchCenter: React.Dispatch<React.SetStateAction<{ lat: number; lng: number } | null>>;
}

const DirectoryContext = createContext<DirectoryContextValue | undefined>(undefined);

export const DirectoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Filters currently being applied to the Directory.
    const [activeFilters, setActiveFilters] = useState<FilterState | undefined>(undefined);
    // Persist search results so they survive navigation to gym profiles.
    const [cachedSearchResults, setCachedSearchResults] = useState<Gym[]>([]);
    // Persist the specific search "key" (location + center) that results belong to.
    const [cachedSearchLocation, setCachedSearchLocation] = useState<string>('');
    const [cachedSearchCenter, setCachedSearchCenter] = useState<{ lat: number; lng: number } | null>(null);

    const value = useMemo<DirectoryContextValue>(() => ({
        activeFilters,
        setActiveFilters,
        cachedSearchResults,
        setCachedSearchResults,
        cachedSearchLocation,
        setCachedSearchLocation,
        cachedSearchCenter,
        setCachedSearchCenter,
    }), [
        activeFilters,
        setActiveFilters,
        cachedSearchResults,
        setCachedSearchResults,
        cachedSearchLocation,
        setCachedSearchLocation,
        cachedSearchCenter,
        setCachedSearchCenter,
    ]);

    return <DirectoryContext.Provider value={value}>{children}</DirectoryContext.Provider>;
};

export const useDirectoryContext = (): DirectoryContextValue => {
    const context = useContext(DirectoryContext);
    if (!context) {
        throw new Error('useDirectoryContext must be used within a DirectoryProvider');
    }
    return context;
};
